import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store';
import { useMobileContext } from '../layouts/MobileLayout';
import { API_BASE_URL } from '../lib/utils';
import PRFMainScreen from '../components/prf/PRFMainScreen';
import PatientDetailsSection from '../components/prf/sections/PatientDetailsSection';
import NOKDetailsSection from '../components/prf/sections/NOKDetailsSection';
import PresentingComplaintSection from '../components/prf/sections/PresentingComplaintSection';
import PastMedicalHistorySection from '../components/prf/sections/PastMedicalHistorySection';
import AllergiesMedsSection from '../components/prf/sections/AllergiesMedsSection';
import FASTSection from '../components/prf/sections/FASTSection';
import ObservationsSection from '../components/prf/sections/ObservationsSection';
import AttendingSection from '../components/prf/sections/AttendingSection';
import ClosingDetailsSection from '../components/prf/sections/ClosingDetailsSection';
import type { 
  PatientReportForm, 
  PatientDetails, 
  NOKDetails, 
  PresentingComplaint,
  PastMedicalHistory,
  AllergiesMeds,
  FAST,
  Observations,
  Attending,
  ClosingDetails,
  PRFSectionStatus
} from '../types/prf';

type SectionId = 'patientDetails' | 'nokDetails' | 'presentingComplaint' | 'pastMedicalHistory' | 'allergiesMeds' | 'fast' | 'observations' | 'attending' | 'closingDetails';

import { generatePRFRef } from '../lib/prfUtils';
import { guardExitForm, hasPrfFormContent } from '../lib/formGuard';
import { resolveMdtFormIncident, resolveQuickActionLocation } from '../lib/mdtFormIncident';

export default function PRFPage() {
  const navigate = useNavigate();
  const reduxDispatch = useAppDispatch();
  const { activeDispatch } = useAppSelector((state) => state.dispatch);
  const { mdtResource } = useMobileContext();
  const [currentSection, setCurrentSection] = useState<SectionId | null>(null);
  const [incidentInfo, setIncidentInfo] = useState<{ eventNumber: string; incidentNumber: string } | null>(null);
  const [prfRef, setPrfRef] = useState<string>('');
  
  // Initialize PRF data
  const [prfData, setPrfData] = useState<Partial<PatientReportForm>>({
    patientDetails: {},
    nokDetails: {},
    presentingComplaint: {},
    pastMedicalHistory: {},
    allergiesMeds: {},
    fast: {},
    observations: {},
    attending: {},
    closingDetails: {},
    sectionStatus: {
      patientDetails: 'not_started',
      nokDetails: 'not_started',
      presentingComplaint: 'not_started',
      pastMedicalHistory: 'not_started',
      allergiesMeds: 'not_started',
      fast: 'not_started',
      observations: 'not_started',
      attending: 'not_started',
      closingDetails: 'not_started',
    },
  });

  useEffect(() => {
    if (activeDispatch && activeDispatch.status === 'accepted') {
      const eventNum = activeDispatch.eventId || '';
      const incidentNum = activeDispatch.incidentId;
      setIncidentInfo({ eventNumber: eventNum, incidentNumber: incidentNum });
      setPrfRef(generatePRFRef(eventNum, incidentNum));
    } else {
      setIncidentInfo(null);
      setPrfRef('');
    }
  }, [activeDispatch]);

  const handleSectionSelect = (sectionId: string) => {
    setCurrentSection(sectionId as SectionId);
  };

  const handleBack = () => {
    setCurrentSection(null);
  };

  const handleSaveSection = (sectionId: SectionId, data: any) => {
    setPrfData(prev => ({
      ...prev,
      [sectionId]: data,
      sectionStatus: {
        ...prev.sectionStatus!,
        [sectionId]: 'complete' as PRFSectionStatus,
      },
    }));
    setCurrentSection(null);
  };

  const token = useAppSelector((s) => s.auth.token);

  const handleSubmit = async () => {
    if (!token) return;
    try {
      let eventId = incidentInfo?.eventNumber;
      let incidentId = incidentInfo?.incidentNumber;
      let ref = prfRef;

      if (!incidentId) {
        const location = await resolveQuickActionLocation();
        const patientName = [
          prfData.patientDetails?.firstName,
          prfData.patientDetails?.lastName,
        ].filter(Boolean).join(' ').trim();
        const description = [
          patientName ? `Patient Report Form — ${patientName}` : 'Patient Report Form',
          prfData.presentingComplaint?.chiefComplaint,
        ].filter(Boolean).join(' | ');

        const resolved = await resolveMdtFormIncident({
          token,
          mdtResource,
          activeDispatch,
          reduxDispatch,
          createTitle: 'Patient Report Form',
          createDescription: `${description} — ${mdtResource?.name || 'MDT unit'} at ${location.address}`,
          createSeverity: 'medium',
          createTags: ['prf', 'patient_report'],
          location,
        });
        eventId = resolved.eventId;
        incidentId = resolved.incidentId;
        ref = generatePRFRef(eventId, incidentId);
        setIncidentInfo({ eventNumber: eventId, incidentNumber: incidentId });
        setPrfRef(ref);
      }

      if (!eventId || !incidentId || !ref) {
        alert('Missing incident or PRF reference');
        return;
      }

      const res = await fetch(`${API_BASE_URL}/prfs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          id: ref,
          eventId,
          incidentId,
          data: prfData,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(`Failed to submit PRF: ${err.error || res.statusText}`);
        return;
      }
      alert(`PRF ${ref} submitted successfully!`);
      navigate(-1);
    } catch {
      alert('Network error submitting PRF');
    }
  };

  // Render current section
  if (currentSection === 'patientDetails') {
    return (
      <PatientDetailsSection
        data={prfData.patientDetails || {}}
        onSave={(data) => handleSaveSection('patientDetails', data)}
        onBack={handleBack}
      />
    );
  }

  if (currentSection === 'nokDetails') {
    return (
      <NOKDetailsSection
        data={prfData.nokDetails || {}}
        onSave={(data) => handleSaveSection('nokDetails', data)}
        onBack={handleBack}
      />
    );
  }

  if (currentSection === 'presentingComplaint') {
    return (
      <PresentingComplaintSection
        data={prfData.presentingComplaint || {}}
        onSave={(data) => handleSaveSection('presentingComplaint', data)}
        onBack={handleBack}
      />
    );
  }

  if (currentSection === 'pastMedicalHistory') {
    return (
      <PastMedicalHistorySection
        data={prfData.pastMedicalHistory || {}}
        onSave={(data) => handleSaveSection('pastMedicalHistory', data)}
        onBack={handleBack}
      />
    );
  }

  if (currentSection === 'allergiesMeds') {
    return (
      <AllergiesMedsSection
        data={prfData.allergiesMeds || {}}
        onSave={(data) => handleSaveSection('allergiesMeds', data)}
        onBack={handleBack}
      />
    );
  }

  if (currentSection === 'fast') {
    return (
      <FASTSection
        data={prfData.fast || {}}
        onSave={(data) => handleSaveSection('fast', data)}
        onBack={handleBack}
      />
    );
  }

  if (currentSection === 'observations') {
    return (
      <ObservationsSection
        data={prfData.observations || {}}
        onSave={(data) => handleSaveSection('observations', data)}
        onBack={handleBack}
      />
    );
  }

  if (currentSection === 'attending') {
    return (
      <AttendingSection
        data={prfData.attending || {}}
        onSave={(data) => handleSaveSection('attending', data)}
        onBack={handleBack}
      />
    );
  }

  if (currentSection === 'closingDetails') {
    return (
      <ClosingDetailsSection
        data={prfData.closingDetails || {}}
        onSave={(data) => handleSaveSection('closingDetails', data)}
        onBack={handleBack}
      />
    );
  }

  const displayIncidentId = incidentInfo?.incidentNumber || (activeDispatch?.status === 'accepted' ? activeDispatch.incidentId : 'On submit');
  
  return (
    <PRFMainScreen
      incidentId={displayIncidentId}
      eventNumber={incidentInfo?.eventNumber}
      prfRef={prfRef || undefined}
      onSectionSelect={handleSectionSelect}
      onBack={() => guardExitForm(hasPrfFormContent(prfData as Record<string, unknown>), () => navigate(-1))}
      onSubmit={handleSubmit}
      sectionStatuses={prfData.sectionStatus}
    />
  );
}
